import React, { useId, useState } from "react";
import { MdCheckCircle, MdChevronLeft, MdChevronRight, MdReplay } from "react-icons/md";
import Button, { IconButton } from "components/button";
import ChoiceOption from "views/public/shared/ChoiceOption";
import PublicSurveyShell from "views/public/shared/PublicSurveyShell";
import SurveyProgress from "views/public/shared/SurveyProgress";

/**
 * A fully working demo of the Pathways guest survey that never writes to the
 * database. Used for customer demos.
 *
 * Route: /demo/pathways-survey
 */

// Matches the real Pathways initial survey.
const DEMO_QUESTIONS = [
  {
    id: "pw_q1_visit_reason",
    text: "What's drawing you to check out our church right now?",
    options: [
      "A friend or family member invited me",
      "I just moved to the area",
      "I've been searching for a church on my own",
      "I'm going through a tough season and looking for support",
    ],
  },
  {
    id: "pw_q2_faith_journey",
    text: "When you think about your faith journey so far, where would you say you are?",
    options: [
      "I'm exploring: faith is pretty new to me",
      "I grew up in church but drifted away for a while",
      "I have a relationship with God and want to go deeper",
      "I'd say I'm pretty established in my faith",
    ],
  },
  {
    id: "pw_q3_life_season",
    text: "Which of these describes the season of life you're in?",
    options: [
      "College-age or young adult",
      "Newly married or newly single",
      "Raising kids (the beautiful chaos years)",
      "Empty nester or retired",
      "Navigating a major life transition",
    ],
  },
  {
    id: "pw_q4_involvement",
    text: "If you were to get involved here, what would make you feel most alive?",
    options: [
      "Serving on a team (worship, kids, hospitality, etc.)",
      "Joining a small group or Bible study",
      "Volunteering for outreach or community impact",
      "Attending classes or workshops to grow in my faith",
      "I'm not sure yet, just taking it one step at a time",
    ],
  },
  {
    id: "pw_q5_church_hope",
    text: "What's one thing you're hoping to find in a church community?",
    options: [
      "Genuine friendships and a sense of belonging",
      "Help growing in my faith and understanding the Bible",
      "Support during a hard season (grief, anxiety, transitions)",
      "A place where my kids and family can thrive",
      "Opportunities to make a difference in the community",
    ],
  },
];

const DEMO_GUEST_NAME = "Sarah";
const DEMO_ORG_NAME = "Demo Church";

const DemoBanner = () => (
  <div className="bg-warning-subtle px-4 py-2 text-center text-xs font-semibold text-warning-fg">
    Demo mode: no data is being saved
  </div>
);

const PathwaysDemoPage = () => {
  const questionId = useId();
  const [flowState, setFlowState] = useState("welcome"); // welcome | questions | completed
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});

  const handleNext = () => {
    if (currentQuestion < DEMO_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setFlowState("completed");
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const handleRestart = () => {
    setFlowState("welcome");
    setCurrentQuestion(0);
    setAnswers({});
  };

  const currentQ = DEMO_QUESTIONS[currentQuestion];
  const isLast = currentQuestion === DEMO_QUESTIONS.length - 1;
  const shellProps = { organizationName: DEMO_ORG_NAME, banner: <DemoBanner /> };

  if (flowState === "questions" && currentQ) {
    return (
      <PublicSurveyShell
        {...shellProps}
        progress={<SurveyProgress current={currentQuestion + 1} total={DEMO_QUESTIONS.length} />}
      >
        <h1 id={questionId} className="mb-6 text-center text-title leading-snug">
          {currentQ.text}
        </h1>
        <div role="group" aria-labelledby={questionId} className="flex flex-col gap-3">
          {currentQ.options.map((option) => (
            <ChoiceOption
              key={option}
              selected={answers[currentQ.id] === option}
              onClick={() => setAnswers((prev) => ({ ...prev, [currentQ.id]: option }))}
            >
              {option}
            </ChoiceOption>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-3">
          <IconButton
            variant="secondary"
            size="lg"
            icon={<MdChevronLeft className="h-6 w-6" />}
            label="Previous question"
            onClick={handleBack}
            disabled={currentQuestion === 0}
          />
          <Button
            size="lg"
            className="min-w-0 flex-1"
            rightIcon={<MdChevronRight aria-hidden="true" className="h-5 w-5" />}
            onClick={handleNext}
            disabled={answers[currentQ.id] === undefined}
          >
            {isLast ? "Submit" : "Continue"}
          </Button>
        </div>
      </PublicSurveyShell>
    );
  }

  if (flowState === "completed") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <div className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-subtle text-success-fg">
            <MdCheckCircle aria-hidden="true" className="h-9 w-9" />
          </span>
          <h1 className="mt-5 text-title">Thank You, {DEMO_GUEST_NAME}</h1>
          <p className="mt-2 text-sm text-fg-muted">Your responses have been received.</p>
          <p className="mx-auto mt-6 max-w-sm text-sm leading-relaxed text-fg">
            Thank you for sharing. Your responses help us serve you better.
          </p>
          <Button
            variant="ghost"
            leftIcon={<MdReplay aria-hidden="true" />}
            onClick={handleRestart}
            className="mt-6"
          >
            Restart Demo
          </Button>
        </div>
      </PublicSurveyShell>
    );
  }

  return (
    <PublicSurveyShell {...shellProps} center>
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-wide text-fg-muted">Atrium Pathways</p>
        <h1 className="mt-2 text-display">Welcome, {DEMO_GUEST_NAME}</h1>
        <p className="mt-3 text-sm text-fg-muted">
          We'd love to hear about your experience. Just 5 quick questions.
        </p>
        <Button
          size="lg"
          rightIcon={<MdChevronRight aria-hidden="true" className="h-5 w-5" />}
          onClick={() => setFlowState("questions")}
          className="mt-8"
        >
          Begin Survey
        </Button>
        <p className="mt-6 text-xs text-fg-muted">
          Takes about 2 minutes · Your answers are confidential
        </p>
      </div>
    </PublicSurveyShell>
  );
};

export default PathwaysDemoPage;
